import {
  mat3,
  type ReadonlyVec2,
  vec2,
  vec4,
  type ReadonlyMat3,
  type ReadonlyVec4,
} from 'gl-matrix';

import { type ReadonlyGraphicPoint } from './points';

/**
 * Represents a colour in a gradient.
 */
export interface GradientStop {
  /**
   * The position of the stop in the gradient, from 0 to 1.
   */
  position: number;

  /**
   * The colour of the stop, represented as an RGBA colour in the range [0, 255].
   */
  color: ReadonlyVec4;
}

/**
 * Represents a colour in a graphic, used when rendering a fill or stroke. It can be a
 * solid colour, linear gradient, or radial gradient.
 */
export type GraphicColor =
  | GraphicSolidColor
  | GraphicStrokeColor
  | GraphicFillColor
  | GraphicLinearGradient
  | GraphicRadialGradient;

/**
 * Represents a solid colour in a graphic.
 */
export class GraphicSolidColor {
  constructor(public color: ReadonlyVec4) {}
}

/**
 * Represents a colour in a graphic that should react to the active stroke colour.
 *
 * Intended to be used for *fills*. Behaviour with strokes is not tested.
 * It makes a fill appear with the stroke color.
 * There is also special behaviour to keep the fill renderer active even if the fill is disabled.
 *
 * This is incompatible with any other color type for fills, in other words it will override all other fill colors defined on the graphic.
 */
export class GraphicStrokeColor {}

/**
 * Represents a colour in a graphic that should react to the active fill colour.
 *
 * Intended to be used for *strokes*. Behaviour with fills is not tested.
 * It makes a stroke appear with the fill color.
 * There is no special behaviour to turn the opacity of the stroke to 0 when the fill is disabled.
 */
export class GraphicFillColor {}

/**
 * Represents a gradient in a graphic.
 */
export abstract class GraphicGradient {
  readonly #stops: GradientStop[] = [];

  /**
   * The transformation matrix to apply to the gradient when rendering it.
   *
   * @remarks Can be used to translate, rotate, or scale the gradient.
   */
  public readonly transform: ReadonlyMat3;

  protected constructor(transform: ReadonlyMat3 = mat3.create()) {
    this.transform = transform;
  }

  /**
   * Adds a stop to the gradient.
   *
   * @param position The position of the stop in the gradient, from 0 to 1.
   * @param color The colour of the stop, represented as an RGBA colour in the range [0,
   * 255].
   */
  addStop(position: number, color: ReadonlyVec4) {
    if (position < 0 || position > 1) {
      throw new Error('Gradient stop position must be between 0 and 1');
    }

    for (let i = 0; i < this.#stops.length; i++) {
      if (this.#stops[i].position > position) {
        this.#stops.splice(i, 0, { position, color });
        return;
      }
    }

    // If we reach here, the new stop is at the end of the array
    this.#stops.push({ position, color });
  }

  /**
   * The stops of the gradient, sorted by position.
   */
  get stops(): readonly GradientStop[] {
    return this.#stops;
  }

  /**
   * Gets the stops of the gradient after flattening them to a fixed number of equally
   * spaced stops.
   *
   * @param stopCount The number of stops to flatten to.
   * @returns An array of the flattened stop colours, in order of their position in the
   * gradient.
   */
  flattenedStops(stopCount: number): vec4[] {
    if (stopCount <= 1) {
      throw new Error('Stop count must be greater than 1');
    }

    if (this.#stops.length === 0) {
      throw new Error('Gradient must have at least one stop');
    }

    const result: vec4[] = [];

    for (let i = 0; i < stopCount; i++) {
      // Calculate the position for this flattened stop
      const position = i / (stopCount - 1);

      let color: vec4;

      if (this.#stops.length === 1) {
        // Single stop - just copy its color to all flattened stops
        color = vec4.clone(this.#stops[0].color as vec4);
      } else if (position <= this.#stops[0].position) {
        // Before the first stop - use its color
        color = vec4.clone(this.#stops[0].color as vec4);
      } else if (position >= this.#stops[this.#stops.length - 1].position) {
        // After the last stop - use its color
        color = vec4.clone(this.#stops[this.#stops.length - 1].color as vec4);
      } else {
        // Find the two stops to interpolate between
        let firstStopIdx = 0;
        for (let j = 0; j < this.#stops.length - 1; j++) {
          if (
            position >= this.#stops[j].position &&
            position <= this.#stops[j + 1].position
          ) {
            firstStopIdx = j;
            break;
          }
        }

        const leftStop = this.#stops[firstStopIdx];
        const rightStop = this.#stops[firstStopIdx + 1];

        // Calculate interpolation factor
        const t =
          (position - leftStop.position) / (rightStop.position - leftStop.position);

        // Interpolate the colors
        color = vec4.create();
        vec4.lerp(color, leftStop.color, rightStop.color, t);
      }

      result.push(color);
    }

    return result;
  }
}

/**
 * Represents a linear gradient in a graphic.
 */
export class GraphicLinearGradient extends GraphicGradient {
  /**
   * The start point of the gradient, in graphic units.
   */
  public readonly start: ReadonlyVec2;

  /**
   * The end point of the gradient, in graphic units.
   */
  public readonly end: ReadonlyVec2;

  /**
   * Creates a new linear gradient.
   *
   * @param start The start point of the gradient, in graphic units.
   * @param end The end point of the gradient, in graphic units.
   * @param transform An optional transformation matrix to apply to the gradient when
   * rendering it.
   */
  constructor(start: ReadonlyVec2, end: ReadonlyVec2, transform?: ReadonlyMat3) {
    super(transform);

    this.start = start;
    this.end = end;
  }
}

/**
 * Represents a radial gradient in a graphic.
 */
export class GraphicRadialGradient extends GraphicGradient {
  /**
   * The center point of the gradient, in graphic units.
   */
  public readonly center: ReadonlyVec2;

  /**
   * The radius of the gradient, in graphic units.
   */
  public readonly radius: number;

  /**
   * Creates a new radial gradient.
   *
   * @param center The center point of the gradient, in graphic units.
   * @param radius The radius of the gradient, in graphic units.
   * @param transform An optional transformation matrix to apply to the gradient when
   * rendering it.
   */
  constructor(center: ReadonlyVec2, radius: number, transform?: ReadonlyMat3) {
    super(transform);

    this.center = center;
    this.radius = radius;
  }
}

/**
 * Represents a serializable definition of a {@link GraphicColor}.
 */
export type GraphicColorDefinition =
  | GraphicSolidColorDefinition
  | GraphicStrokeColorDefinition
  | GraphicFillColorDefinition
  | GraphicLinearGradientDefinition
  | GraphicRadialGradientDefinition;

/**
 * Represents a serializable definition of a {@link GraphicSolidColor}.
 */
export interface GraphicSolidColorDefinition {
  type: 'solid';
  color: ReadonlyVec4;
}

/**
 * Represents a serializable definition of a {@link GraphicStrokeColor}.
 */
export interface GraphicStrokeColorDefinition {
  type: 'stroke';
}

/**
 * Represents a serializable definition of a {@link GraphicFillColor}.
 */
export interface GraphicFillColorDefinition {
  type: 'fill';
}

/**
 * Represents a serializable definition of a {@link GraphicGradient}.
 */
interface GraphicGradientDefinitionBase {
  /**
   * An optional transformation matrix to apply to the gradient when rendering it.
   */
  transform?: ReadonlyMat3;

  /**
   * The colour stops of the gradient.
   */
  stops: GradientStop[];
}

/**
 * Represents a serializable definition of a {@link GraphicLinearGradient}.
 */
export interface GraphicLinearGradientDefinition extends GraphicGradientDefinitionBase {
  type: 'linear-gradient';

  /**
   * The start point of the gradient, in graphic units.
   */
  start: ReadonlyVec2;

  /**
   * The end point of the gradient, in graphic units.
   */
  end: ReadonlyVec2;
}

/**
 * Represents a serializable definition of a {@link GraphicRadialGradient}.
 */
export interface GraphicRadialGradientDefinition extends GraphicGradientDefinitionBase {
  type: 'radial-gradient';

  /**
   * The center point of the gradient, in graphic units.
   */
  center: ReadonlyVec2;

  /**
   * The radius of the gradient, in graphic units.
   */
  radius: number;
}

/**
 * Helper class to build a {@link GraphicLinearGradientDefinition}.
 */
export class GraphicLinearGradientBuilder {
  readonly #stops: GradientStop[] = [];
  #start: ReadonlyVec2 = vec2.fromValues(0, 0);
  #end: ReadonlyVec2 = vec2.fromValues(1, 0);
  #transform: ReadonlyMat3 | undefined = undefined;

  /**
   * Sets the start point of the linear gradient.
   *
   * @param start The start point of the gradient, in graphic units.
   * @returns This builder instance for chaining.
   */
  setStart(start: ReadonlyVec2): this {
    this.#start = start;
    return this;
  }

  /**
   * Sets the end point of the linear gradient.
   *
   * @param end The end point of the gradient, in graphic units.
   * @returns This builder instance for chaining.
   */
  setEnd(end: ReadonlyVec2): this {
    this.#end = end;
    return this;
  }

  /**
   * Sets the transformation matrix to apply to the gradient when rendering it.
   *
   * @param transform The transformation matrix.
   * @returns This builder instance for chaining.
   */
  setTransform(transform: ReadonlyMat3): this {
    this.#transform = transform;
    return this;
  }

  /**
   * Adds a stop to the linear gradient.
   *
   * @param position The position of the stop in the gradient, from 0 to 1.
   * @param color The colour of the stop, represented as an RGBA colour in the range [0,
   * 255].
   * @returns This builder instance for chaining.
   */
  addStop(position: number, color: ReadonlyVec4): this {
    this.#stops.push({ position, color });
    return this;
  }

  /**
   * Builds the {@link GraphicLinearGradientDefinition}.
   *
   * @returns The constructed linear gradient definition.
   */
  build(): GraphicLinearGradientDefinition {
    return {
      type: 'linear-gradient',
      start: this.#start,
      end: this.#end,
      transform: this.#transform,
      stops: this.#stops,
    };
  }
}

/**
 * Helper class to build a {@link GraphicRadialGradientDefinition}.
 */
export class GraphicRadialGradientBuilder {
  readonly #stops: GradientStop[] = [];
  #center: ReadonlyVec2 = vec2.fromValues(0.5, 0.5);
  #radius = 0.5;
  #transform: ReadonlyMat3 | undefined = undefined;

  /**
   * Sets the center of the radial gradient.
   *
   * @param center The center point of the gradient, in graphic units.
   * @returns This builder instance for chaining.
   */
  setCenter(center: ReadonlyVec2): this {
    this.#center = center;
    return this;
  }

  /**
   * Sets the radius of the radial gradient.
   *
   * @param radius The radius of the gradient, in graphic units.
   * @returns This builder instance for chaining.
   */
  setRadius(radius: number): this {
    this.#radius = radius;
    return this;
  }

  /**
   * Sets the transformation matrix to apply to the gradient when rendering it.
   *
   * @param transform The transformation matrix.
   * @returns This builder instance for chaining.
   */
  setTransform(transform: ReadonlyMat3): this {
    this.#transform = transform;
    return this;
  }

  /**
   * Adds a stop to the radial gradient.
   *
   * @param position The position of the stop in the gradient, from 0 to 1.
   * @param color The colour of the stop, represented as an RGBA colour in the range [0,
   * 255].
   * @returns This builder instance for chaining.
   */
  addStop(position: number, color: ReadonlyVec4): this {
    this.#stops.push({ position, color });
    return this;
  }

  /**
   * Builds the {@link GraphicRadialGradientDefinition}.
   *
   * @returns The constructed radial gradient definition.
   */
  build(): GraphicRadialGradientDefinition {
    return {
      type: 'radial-gradient',
      center: this.#center,
      radius: this.#radius,
      transform: this.#transform,
      stops: this.#stops,
    };
  }
}

/**
 * Converts a {@link GraphicColorDefinition} to a {@link GraphicColor}.
 *
 * @param definition The color definition to convert.
 * @returns The corresponding {@link GraphicColor} instance, or `undefined` if the the
 * definition was `undefined`.
 */
export function colorDefinitionToColor(
  definition: GraphicColorDefinition | undefined
): GraphicColor | undefined {
  if (definition?.type === 'solid') {
    return new GraphicSolidColor(definition.color);
  } else if (definition?.type === 'stroke') {
    return new GraphicStrokeColor();
  } else if (definition?.type === 'fill') {
    return new GraphicFillColor();
  } else if (
    definition?.type === 'linear-gradient' ||
    definition?.type === 'radial-gradient'
  ) {
    const gradient =
      definition.type === 'linear-gradient'
        ? new GraphicLinearGradient(
            definition.start,
            definition.end,
            definition.transform
          )
        : new GraphicRadialGradient(
            definition.center,
            definition.radius,
            definition.transform
          );

    for (const stop of definition.stops) {
      gradient.addStop(stop.position, stop.color);
    }

    return gradient;
  }

  return undefined;
}

/**
 * Creates a transformation matrix for a gradient to match the coordinate normalization
 * applied to a Graphic.
 *
 * When a Graphic is rendered, its coordinates are normalized so that the top-left corner
 * of its bounding box maps to (0, 0). Optionally, the coordinates can also be scaled so
 * that the bottom-right corner maps to (1, 1), creating a unit square.
 *
 * Since gradients are defined in the original coordinate space of the graphic, they need
 * to be transformed to match this normalization. This function creates the appropriate
 * transformation matrix.
 *
 * @param boundsMin The top-left corner of the graphic's bounding box in the original
 * coordinate space.
 * @param boundsSize The width and height of the graphic's bounding box in the original
 * coordinate space.
 * @param shouldNormalise If `true`, scales the gradient so that the bounding box maps to
 * a unit square (0, 0) to (1, 1). If `false`, only translates the gradient so that the
 * top-left corner maps to (0, 0).
 * @param existingTransform An optional existing transformation matrix to before the
 * normalization transform. This allows for additional gradient-specific transformations
 * (e.g., rotation, additional scaling). Defaults to an identity matrix.
 * @returns A transformation matrix that, when applied to a gradient, will match the
 * coordinate normalization of the graphic.
 */
export function computeGradientNormalizationTransform(
  boundsMin: ReadonlyGraphicPoint,
  boundsSize: ReadonlyGraphicPoint,
  shouldNormalise: boolean,
  existingTransform?: ReadonlyMat3
) {
  const transform = mat3.create();

  // Scale the gradient to fit the bounds
  if (shouldNormalise) {
    const scale = vec2.fromValues(
      1 / boundsSize.resolveAtUnitSizeForAxis('x'),
      1 / boundsSize.resolveAtUnitSizeForAxis('y')
    );
    mat3.scale(transform, transform, scale);
  }

  // Move the gradient so that the top-left corner of the bounds maps to (0, 0)
  const translation = boundsMin.resolveAtUnitSize(vec2.create());
  vec2.negate(translation, translation);
  mat3.translate(transform, transform, translation);

  // Finally, apply the existing transform
  if (existingTransform) {
    mat3.multiply(transform, transform, existingTransform);
  }

  return transform;
}
